import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { IoMdArrowDropdown, IoMdArrowDropup } from "react-icons/io";
import WaterMark from "../../../Components/WaterMark/WaterMark";
import { Loader } from "rsuite";
import { TbHandClick } from "react-icons/tb";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { axInst } from "../../../Components/Login/Service";

const region_table_details = async (body) => {
  try {
    const res = await axInst.post("/pipeline/region-wise-tqp", body);
    const obj = res.data;

    const mtd_sum = Object.values(obj).reduce((sum, e) => sum + e.mtd, 0);
    const lmtd_sum = Object.values(obj).reduce((sum, e) => sum + e.lmtd, 0);

    const formattedData = Object.keys(obj).map((region) => ({
      Region: region,
      MTD: obj[region].mtd,
      LMTD: obj[region].lmtd,
      States: [],
    }));

    return {
      obj: formattedData,
      mtd_sum,
      lmtd_sum,
    };
  } catch (error) {
    console.error(error);
    return {
      obj: [],
      mtd_sum: 0,
      lmtd_sum: 0,
    };
  }
};

const state_table_details = async (requestBody) => {
  try {
    const res = await axInst.post("pipeline/state-wise-tqp", requestBody);
    return res.data;
  } catch (error) {
    console.error(error);
    return {};
  }
};

const dealerwise_table_details = async (requestBody2) => {
  try {
    const res = await axInst.post("pipeline/dealer-wise-tqp", requestBody2);
    return res.data;
  } catch (error) {
    console.error(error);
    return {};
  }
};

export const TqpStatus = () => {
  const { data: filterdata, submitted } = useSelector(
    (state) => state.filtersPipeline
  );

  const { data, isFetching, isError } = useQuery({
    queryKey: ["tqpTable", submitted],
    queryFn: () => region_table_details(filterdata),
    initialData: {
      obj: [],
      mtd_sum: 0,
      lmtd_sum: 0,
    },
    refetchInterval: 300000,
    refetchIntervalInBackground: true,
    cacheTime: 0,
    staleTime: 0,
  });

  const [sorting, setSorting] = useState([]);
  const [filtering, setFiltering] = useState("");
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    return () => {
      setExpandedRows({});
    };
  }, []);

  const toggleRowExpanded = async (region, state = null, filterdata) => {
    try {
      if (state) {
        // --- State → Dealer Expansion ---
        const requestBody = {
          ...filterdata, // preserve user-applied filters
          region: [region], // override region
          state: [state], // override state
        };

        if (!expandedRows[region]?.[state]?.isExpanded) {
          const dealerData = await dealerwise_table_details(requestBody);

          setExpandedRows((prev) => ({
            ...prev,
            [region]: {
              ...prev[region],
              [state]: {
                ...prev[region]?.[state],
                isExpanded: true,
                dealerData,
              },
            },
          }));
        } else {
          // collapse state row
          setExpandedRows((prev) => ({
            ...prev,
            [region]: {
              ...prev[region],
              [state]: {
                ...prev[region]?.[state],
                isExpanded: false,
              },
            },
          }));
        }
      } else {
        // --- Region → State Expansion ---
        if (!expandedRows[region]) {
          const requestBody = {
            ...filterdata, // preserve user-applied filters
            region: [region], // override region
            state: [], // reset state
          };

          const stateData = await state_table_details(requestBody);

          // build state expansion map
          const stateExpansion = Object.keys(stateData).reduce(
            (acc, key) => ({
              ...acc,
              [key]: {
                isExpanded: false,
                data: stateData[key] || {},
              },
            }),
            {}
          );

          setExpandedRows((prev) => ({
            ...prev,
            [region]: stateExpansion,
          }));
        } else {
          // collapse region row
          setExpandedRows((prev) => ({
            ...prev,
            [region]: false,
          }));
        }
      }
    } catch (error) {
      console.error("Error toggling row expansion:", error);
    }
  };

  const columns = [
    {
      id: "region",
      header: "Region",
      accessorFn: (value) => value.Region,
      cell: (info) => (
        <span
          className="text-center pl-5 w-5"
          title="Click to view state-wise offtake"
        >
          {info?.getValue()}
        </span>
      ),
      size: 200,
    },
    {
      header: "MTD",
      accessorFn: (value) => value.MTD,
      cell: ({ row }) => (
        <span className="text-right pl-5">{row?.original.MTD}</span>
      ),
    },
    {
      header: "LMTD",
      accessorFn: (value) => value.LMTD,
      cell: ({ row }) => (
        <span className="text-right pl-5">{row?.original.LMTD}</span>
      ),
    },
  ];

  const table = useReactTable({
    data: data?.obj ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting: sorting,
      globalFilter: filtering,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setFiltering,
  });

  return (
    <div className="p-[6px]  relative">
      <header className="pb-1 text-base font-semibold text-[#4c4c4c]">
        Region, State & Dealer Wise TQP
      </header>
      <div className="self-center overflow-auto h-[14.4rem] ">
        {isError && (
          <div className="grid items-center justify-center w-full h-full">
            <p>Error Occurred</p>
          </div>
        )}
        {isFetching && (
          <div className="grid items-center justify-center w-full h-full">
            <Loader />
          </div>
        )}
        {!isFetching && !isError && !data && (
          <div className="grid items-center justify-center w-full h-full">
            <p>Data Not Available</p>
          </div>
        )}
        {!isFetching && !isError && data && (
          <>
            <WaterMark />
            <table className="w-full">
              <thead className="sticky text-white top-0 bg-[#2A5775] text-left z-[1]">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="p-[2px] pl-5 text-sm lg:text-base border text-left border-gray-300"
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row, rowIndex) => (
                  <React.Fragment key={row.id}>
                    <tr
                      className={`even:bg-gray-200 odd:bg-white ${
                        rowIndex === 0 ? "cursor-default" : "cursor-pointer"
                      } ${
                        expandedRows[row.original.Region] ? "font-bold" : ""
                      }`}
                      onClick={() =>
                        row.original.Region !== "All Regions" &&
                        toggleRowExpanded(row.original.Region, null, filterdata)
                      }
                    >
                      {row.getVisibleCells().map((cell, cellIndex) => (
                        <td
                          key={cell.id}
                          className="p-[2px] text-xs  lg:text-sm border border-gray-300 h-[30px] relative"
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                          {cellIndex === 0 &&
                            row.original.Region !== "All Regions" && (
                              <div className="absolute left-20 top-2">
                                {expandedRows[row.original.Region] ? (
                                  <IoMdArrowDropup size={14} />
                                ) : (
                                  <IoMdArrowDropdown size={14} />
                                )}
                              </div>
                            )}
                        </td>
                      ))}
                    </tr>
                    {expandedRows[row.original.Region] &&
                      Object.entries(expandedRows[row.original.Region]).map(
                        ([state, stateDetails]) => (
                          <React.Fragment key={state}>
                            <tr
                              className={`bg-[#2A5775] text-white ${
                                stateDetails.isExpanded
                                  ? "font-bold"
                                  : "font-normal"
                              }`}
                              onClick={() =>
                                toggleRowExpanded(
                                  row.original.Region,
                                  state,
                                  filterdata
                                )
                              }
                            >
                              <td className="p-1 text-sm  lg:text-sm border border-gray-300 h-8 relative">
                                <span className="ml-4">{state}</span>
                                <div className="absolute left-36 top-2">
                                  {stateDetails.isExpanded ? (
                                    <IoMdArrowDropup size={14} />
                                  ) : (
                                    <IoMdArrowDropdown size={14} />
                                  )}
                                </div>
                              </td>
                              <td className="p-1 pl-6 text-xs text-left lg:text-sm border border-gray-300 h-8">
                                {stateDetails.data.mtd}
                              </td>
                              <td className="p-1 pl-6 text-xs text-left lg:text-sm border border-gray-300 h-8">
                                {stateDetails.data.lmtd}
                              </td>
                            </tr>
                            {stateDetails.isExpanded &&
                              Object.entries(stateDetails.dealerData).map(
                                ([dealer, dealerDetails]) => (
                                  <tr
                                    key={dealer}
                                    className="even:bg-gray-50 odd:bg-white text-[12px]"
                                  >
                                    <td className="p-1 text-sm  lg:text-sm border border-gray-300 h-8">
                                      <span className="ml-8 text-[12px]">
                                        {dealer}
                                      </span>
                                    </td>
                                    <td className="p-1 text-sm text-center lg:text-sm border border-gray-300 h-8">
                                      {dealerDetails.mtd}
                                    </td>
                                    <td className="p-1 text-sm text-center lg:text-sm border border-gray-300 h-8">
                                      {dealerDetails.lmtd}
                                    </td>
                                  </tr>
                                )
                              )}
                          </React.Fragment>
                        )
                      )}
                  </React.Fragment>
                ))}
                <tr className="bg-[#1C5982]  text-white text-xs lg:text-sm">
                  <td className="p-[2px] pl-5  lg:text-base border border-gray-300">
                    All Regions
                  </td>
                  <td className="p-[2px] pl-5  lg:text-base border border-gray-300">
                    {data?.mtd_sum ?? 0}
                  </td>
                  <td className="p-[2px] pl-5 lg:text-base border border-gray-300">
                    {data?.lmtd_sum ?? 0}
                  </td>
                </tr>
              </tbody>
            </table>
            <p className="text-xs mt-1 flex items-center">
              <TbHandClick /> on Region to view States, Dealer Wise TQP
            </p>
          </>
        )}
      </div>
    </div>
  );
};
